import "./organize-pdf.css";
import { pdfjs } from "./pdfjs-adapter.js";
import {
  createPageItems,
  deleteSelectedPages,
  hasPdfHeader,
  movePage,
  organizedFileName,
  pagesRemovedFileName,
  rotateSelectedPages,
  rotatedFileName,
  selectedPagesFileName,
  setAllSelected,
  togglePageSelection,
  validateOrganizeFile,
  validateOrganizePageCount
} from "./organize-pdf-core.js";
import { exportOrganizedPdf } from "./organize-pdf-export.js";
import { createOperationResult } from "./operation-result.js";

const root = document.querySelector("[data-organize-pdf]");

if (root) {
  const mode = root.dataset.organizeMode || "organize";
  const config = {
    organize: { reorder: true, rotate: true, delete: true, extract: true, help: "Drag pages or use the arrow buttons. Select pages to rotate, remove, or export them.", button: "Finish PDF", output: organizedFileName },
    rotate: { reorder: false, rotate: true, delete: false, extract: false, help: "Select one or more pages, rotate them left or right, then create the result.", button: "Finish PDF", output: rotatedFileName },
    delete: { reorder: false, rotate: false, delete: true, extract: false, help: "Select the pages you do not need, remove them, then create the result.", button: "Finish PDF", output: pagesRemovedFileName },
    extract: { reorder: false, rotate: false, delete: false, extract: true, help: "Select any pages to save them together as a new PDF.", button: "Finish PDF", output: organizedFileName }
  }[mode] || null;
  if (!config) throw new Error(`Unknown organizer mode: ${mode}`);
  const ids = [
    "organizeStatus", "organizeUploadPanel", "organizeDropZone", "chooseOrganizePdf", "organizeFileInput",
    "organizeWorkspace", "organizeFileName", "organizePageCount", "organizeSelectionCount", "replaceOrganizePdf",
    "resetOrganizePdf", "downloadOrganizedPdf", "organizeProgress", "organizePageList", "selectAllPages",
    "clearPageSelection", "rotatePagesLeft", "rotatePagesRight", "deleteSelectedPages", "exportSelectedPages"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = {
    bytes: null,
    document: null,
    fileName: "document.pdf",
    originalRotations: [],
    items: [],
    busy: false,
    draggedId: null
  };
  const operationResult = createOperationResult(root, elements.organizeWorkspace);

  document.getElementById("organizeWorkspaceHelp").textContent = config.help;
  elements.downloadOrganizedPdf.textContent = config.button;
  elements.downloadOrganizedPdf.hidden = mode === "extract";
  root.querySelectorAll('[data-organize-feature="rotate"]').forEach(element => { element.hidden = !config.rotate; });
  root.querySelectorAll('[data-organize-feature="delete"]').forEach(element => { element.hidden = !config.delete; });
  root.querySelectorAll('[data-organize-feature="extract"]').forEach(element => { element.hidden = !config.extract; });

  function showStatus(message) {
    elements.organizeStatus.textContent = message;
    elements.organizeStatus.hidden = !message;
  }

  function clearStatus() {
    showStatus("");
  }

  function openWorkspace() {
    elements.organizeUploadPanel.hidden = true;
    elements.organizeWorkspace.hidden = false;
    root.classList.add("is-active");
    document.body.classList.add("organize-active");
  }

  function selectedItems() {
    return state.items.filter(item => item.selected);
  }

  function updateControls() {
    const selected = selectedItems().length;
    elements.organizePageCount.textContent = String(state.items.length);
    elements.organizeSelectionCount.textContent = String(selected);
    elements.organizeFileName.textContent = state.fileName;
    elements.rotatePagesLeft.disabled = state.busy || !selected;
    elements.rotatePagesRight.disabled = state.busy || !selected;
    elements.deleteSelectedPages.disabled = state.busy || !selected;
    elements.exportSelectedPages.disabled = state.busy || !selected;
    elements.selectAllPages.disabled = state.busy || selected === state.items.length;
    elements.clearPageSelection.disabled = state.busy || !selected;
    elements.replaceOrganizePdf.disabled = state.busy;
    elements.resetOrganizePdf.disabled = state.busy;
    elements.downloadOrganizedPdf.disabled = state.busy || !state.items.length;
  }

  function render() {
    elements.organizePageList.replaceChildren();
    state.items.forEach((item, index) => elements.organizePageList.append(createPageCard(item, index)));
    updateControls();
  }

  function createPageCard(item, index) {
    const card = document.createElement("article");
    card.className = `organize-card${item.selected ? " is-selected" : ""}`;
    card.dataset.id = item.id;
    card.draggable = config.reorder && !state.busy;
    card.setAttribute("aria-label", `Page ${index + 1}, originally page ${item.sourceIndex + 1}`);

    const selection = document.createElement("label");
    selection.className = "organize-card-selection";
    selection.title = `Select page ${index + 1}`;
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = item.selected;
    checkbox.setAttribute("aria-label", `Select page ${index + 1}`);
    checkbox.disabled = state.busy;
    checkbox.addEventListener("change", () => {
      state.items = togglePageSelection(state.items, item.id);
      render();
    });
    selection.append(checkbox);

    const number = document.createElement("span");
    number.className = "organize-page-number";
    number.textContent = String(index + 1);

    const preview = document.createElement("div");
    preview.className = "organize-preview";
    if (item.thumbnailUrl) {
      const image = document.createElement("img");
      image.src = item.thumbnailUrl;
      image.alt = "";
      preview.append(image);
    } else {
      preview.textContent = "Rendering page…";
    }

    const meta = document.createElement("div");
    meta.className = "organize-card-meta";
    const source = document.createElement("strong");
    source.textContent = `Original ${item.sourceIndex + 1}`;
    const rotation = document.createElement("span");
    rotation.textContent = item.rotation ? `${item.rotation}°` : "Upright";
    meta.append(source, rotation);

    const actions = document.createElement("div");
    if (config.reorder) {
      actions.className = "organize-card-actions";
      actions.append(
        actionButton("←", `Move page ${index + 1} earlier`, "previous", state.busy || index === 0),
        actionButton("→", `Move page ${index + 1} later`, "next", state.busy || index === state.items.length - 1)
      );
      actions.addEventListener("click", event => handleCardAction(event, item.id));
    }

    card.addEventListener("click", event => {
      if (state.busy || event.target.closest("button, input, label")) return;
      state.items = togglePageSelection(state.items, item.id);
      render();
    });
    card.addEventListener("dragstart", event => {
      if (!config.reorder) { event.preventDefault(); return; }
      if (state.busy) { event.preventDefault(); return; }
      state.draggedId = item.id;
      card.classList.add("is-dragging");
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", item.id);
    });
    card.addEventListener("dragend", clearDragState);
    card.addEventListener("dragover", event => {
      if (!state.busy) { event.preventDefault(); card.classList.add("is-drop-target"); }
    });
    card.addEventListener("dragleave", () => card.classList.remove("is-drop-target"));
    card.addEventListener("drop", event => {
      event.preventDefault();
      if (!state.busy) reorderById(state.draggedId || event.dataTransfer.getData("text/plain"), item.id);
    });

    card.append(selection, number, preview, meta);
    if (config.reorder) card.append(actions);
    return card;
  }

  function actionButton(text, label, action, disabled) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = text;
    button.title = label;
    button.setAttribute("aria-label", label);
    button.dataset.action = action;
    button.disabled = disabled;
    return button;
  }

  function handleCardAction(event, id) {
    const action = event.target.closest("button")?.dataset.action;
    const index = state.items.findIndex(item => item.id === id);
    if (!action || index < 0 || state.busy) return;
    if (action === "previous") state.items = movePage(state.items, index, index - 1);
    if (action === "next") state.items = movePage(state.items, index, index + 1);
    render();
  }

  function reorderById(sourceId, targetId) {
    const from = state.items.findIndex(item => item.id === sourceId);
    const to = state.items.findIndex(item => item.id === targetId);
    state.items = movePage(state.items, from, to);
    clearDragState();
    render();
  }

  function clearDragState() {
    state.draggedId = null;
    document.querySelectorAll(".organize-card").forEach(card => card.classList.remove("is-dragging", "is-drop-target"));
  }

  async function loadFile(file) {
    if (state.busy) return;
    clearStatus();
    const metadataError = validateOrganizeFile(file);
    if (metadataError) { showStatus(metadataError); return; }

    state.busy = true;
    updateControls();
    elements.organizeProgress.hidden = false;
    elements.organizeProgress.textContent = "Reading PDF…";
    let candidateDocument = null;
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (!hasPdfHeader(bytes)) throw new Error("File content is not a valid PDF.");
      candidateDocument = await pdfjs.getDocument({ data: bytes.slice(), password: "" }).promise;
      const pageError = validateOrganizePageCount(candidateDocument.numPages);
      if (pageError) throw new Error(pageError);

      const rotations = [];
      for (let index = 1; index <= candidateDocument.numPages; index += 1) {
        const page = await candidateDocument.getPage(index);
        rotations.push(page.rotate || 0);
        page.cleanup();
      }

      revokeThumbnails(state.items);
      if (state.document) await state.document.destroy();
      state.bytes = bytes;
      state.document = candidateDocument;
      state.fileName = file.name || "document.pdf";
      state.originalRotations = rotations;
      state.items = createPageItems(rotations);
      candidateDocument = null;
      openWorkspace();
      render();
      await refreshThumbnails(state.items);
    } catch (error) {
      if (candidateDocument) await candidateDocument.destroy();
      const message = error?.name === "PasswordException"
        ? "Password-protected PDFs are not supported."
        : error?.message || "smallpdf.app could not read this PDF.";
      showStatus(message);
    } finally {
      state.busy = false;
      elements.organizeProgress.hidden = true;
      elements.organizeFileInput.value = "";
      render();
    }
  }

  async function refreshThumbnails(items) {
    if (!state.document || !items.length) return;
    let completed = 0;
    elements.organizeProgress.hidden = false;
    elements.organizeProgress.textContent = `Rendering 0 of ${items.length}`;
    await processWithConcurrency(items, 3, async item => {
      const page = await state.document.getPage(item.sourceIndex + 1);
      try {
        const nextUrl = await renderThumbnail(page, item.rotation);
        if (item.thumbnailUrl) URL.revokeObjectURL(item.thumbnailUrl);
        item.thumbnailUrl = nextUrl;
      } finally {
        page.cleanup();
      }
      completed += 1;
      elements.organizeProgress.textContent = `Rendering ${completed} of ${items.length}`;
    });
    render();
  }

  async function processWithConcurrency(items, concurrency, task) {
    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) await task(items[cursor++]);
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  }

  async function renderThumbnail(page, rotation) {
    const natural = page.getViewport({ scale: 1, rotation });
    const scale = Math.min(170 / natural.width, 190 / natural.height, 1.25);
    const viewport = page.getViewport({ scale, rotation });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    await page.render({ canvasContext: canvas.getContext("2d", { alpha: false }), viewport }).promise;
    const blob = await new Promise((resolve, reject) => canvas.toBlob(
      value => value ? resolve(value) : reject(new Error("Could not create a page preview.")),
      "image/png"
    ));
    canvas.width = 1;
    canvas.height = 1;
    return URL.createObjectURL(blob);
  }

  async function rotateSelected(delta) {
    if (state.busy || !selectedItems().length) return;
    clearStatus();
    state.busy = true;
    state.items = rotateSelectedPages(state.items, delta);
    render();
    try {
      await refreshThumbnails(selectedItems());
    } catch (error) {
      showStatus(error?.message || "Could not refresh the page previews.");
    } finally {
      state.busy = false;
      elements.organizeProgress.hidden = true;
      render();
    }
  }

  async function resetDocument() {
    if (state.busy || !state.document) return;
    clearStatus();
    state.busy = true;
    revokeThumbnails(state.items);
    state.items = createPageItems(state.originalRotations);
    render();
    try {
      await refreshThumbnails(state.items);
    } catch (error) {
      showStatus(error?.message || "Could not reset the page previews.");
    } finally {
      state.busy = false;
      elements.organizeProgress.hidden = true;
      render();
    }
  }

  function removeSelected() {
    if (state.busy) return;
    clearStatus();
    const result = deleteSelectedPages(state.items);
    if (result.error) { showStatus(result.error); return; }
    const retained = new Set(result.items.map(item => item.id));
    revokeThumbnails(state.items.filter(item => !retained.has(item.id)));
    state.items = result.items;
    render();
  }

  async function downloadPages(pages, fileName) {
    if (state.busy || !pages.length) return;
    clearStatus();
    state.busy = true;
    elements.organizeProgress.hidden = false;
    render();
    try {
      const bytes = await exportOrganizedPdf(state.bytes, pages, (current, total) => {
        elements.organizeProgress.textContent = `Exporting page ${current} of ${total}`;
      });
      operationResult.show({
        blob: new Blob([bytes], { type: "application/pdf" }),
        fileName,
        message: mode === "extract" ? "Your selected pages were extracted successfully." : "Your PDF was created successfully.",
        details: [`${pages.length} ${pages.length === 1 ? "page" : "pages"}`]
      });
    } catch (error) {
      showStatus(error?.message || "smallpdf.app could not export this PDF.");
    } finally {
      state.busy = false;
      elements.organizeProgress.hidden = true;
      render();
    }
  }

  function revokeThumbnails(items) {
    items.forEach(item => { if (item.thumbnailUrl) URL.revokeObjectURL(item.thumbnailUrl); });
  }

  elements.chooseOrganizePdf.addEventListener("click", event => {
    event.stopPropagation();
    elements.organizeFileInput.click();
  });
  elements.replaceOrganizePdf.addEventListener("click", () => elements.organizeFileInput.click());
  elements.organizeFileInput.addEventListener("change", () => loadFile(elements.organizeFileInput.files?.[0]));
  elements.organizeDropZone.addEventListener("click", event => {
    if (!event.target.closest("button")) elements.organizeFileInput.click();
  });
  elements.organizeDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      elements.organizeFileInput.click();
    }
  });
  elements.organizeDropZone.addEventListener("dragover", event => {
    event.preventDefault();
    elements.organizeDropZone.classList.add("is-dragging");
  });
  elements.organizeDropZone.addEventListener("dragleave", () => elements.organizeDropZone.classList.remove("is-dragging"));
  elements.organizeDropZone.addEventListener("drop", event => {
    event.preventDefault();
    elements.organizeDropZone.classList.remove("is-dragging");
    loadFile(event.dataTransfer.files?.[0]);
  });
  elements.selectAllPages.addEventListener("click", () => { state.items = setAllSelected(state.items, true); render(); });
  elements.clearPageSelection.addEventListener("click", () => { state.items = setAllSelected(state.items, false); render(); });
  elements.rotatePagesLeft.addEventListener("click", () => rotateSelected(-90));
  elements.rotatePagesRight.addEventListener("click", () => rotateSelected(90));
  elements.deleteSelectedPages.addEventListener("click", removeSelected);
  elements.resetOrganizePdf.addEventListener("click", resetDocument);
  elements.downloadOrganizedPdf.addEventListener("click", () => downloadPages(state.items, config.output(state.fileName)));
  elements.exportSelectedPages.addEventListener("click", () => downloadPages(selectedItems(), selectedPagesFileName(state.fileName)));
  window.addEventListener("beforeunload", () => {
    operationResult.dispose();
    revokeThumbnails(state.items);
    if (state.document) state.document.destroy();
  });
}
